import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  RefreshCw,
  Loader2,
  Minus,
  Plus,
  X,
  ExternalLink,
} from 'lucide-react';

// Scheduler is loaded via script tag in public/index.html
// CSS: public/lib/dhtmlx-scheduler/dhtmlxscheduler.css
// JS: public/lib/dhtmlx-scheduler/dhtmlxscheduler.js

import { Breadcrumb } from '../components/Breadcrumb';
import { Button } from '../components/ui';
import { useScheduleData } from '../hooks/useScheduleData';
import { apiService } from '../services/apiService';
import type {
  SchedulingBoardEntry,
  ProductionCalendar,
} from '../services/types';
import { getWeekMonday, formatDateKey } from '../utils/scheduleCache';
import { parseCalendarDate } from '../utils/calendarDate';

// Buffer size for fetching (2 weeks before, 4 weeks ahead)
const WEEKS_BEFORE = 2;
const WEEKS_AHEAD = 4;

// Production calendar ID for working hours
const CALENDAR_ID = '2-SHIFTS';

// URL param keys
const URL_PARAM_WEEK = 'week';
const URL_PARAM_FILTER = 'filter';

// Resource group filter options
const RESOURCE_GROUP_FILTERS = [
  { id: 'TN', label: 'TN' },
  { id: 'FN', label: 'FN' },
  { id: 'CONVEN', label: 'CONVEN' },
] as const;

const DEFAULT_FILTER = 'TN';

// Zoom level configurations
const ZOOM_LEVELS = [
  { id: '3d', label: '3D', x_step: 3, x_size: 24, x_date: '%H' }, // 3-hour blocks
  { id: '1w', label: '1W', x_step: 6, x_size: 28, x_date: '%H' }, // 6-hour blocks (default)
  { id: '2w', label: '2W', x_step: 12, x_size: 28, x_date: '%H' }, // 12-hour blocks
  { id: '3w', label: '3W', x_step: 24, x_size: 21, x_date: '' }, // 24-hour blocks
  { id: '1m', label: '1M', x_step: 24, x_size: 30, x_date: '' }, // 24-hour blocks
  { id: '2m', label: '2M', x_step: 24, x_size: 60, x_date: '' }, // 24-hour blocks
] as const;

const DEFAULT_ZOOM = '1w';

// Continuous-scroll architecture: render N windows worth of timeline at once
// so the user can pan smoothly through 3 windows of content before a silent
// rebase is needed. The middle window (index 1) is what the user initially
// looks at, with one window of buffer on each side.
const RENDERED_WINDOWS = 3;
const INITIAL_VISIBLE_INDEX = 1; // 0 = leftmost, RENDERED_WINDOWS-1 = rightmost
// Pixel width per timeline column (matches column_width passed to DHTMLX)
const COLUMN_WIDTH_PX = 60;

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Format a date range for the navbar label. Matches the style DHTMLX renders
 * by default ("11 May 2026 – 31 May 2026") but collapses redundant pieces
 * when the dates share a month or year.
 */
function formatDateRangeLabel(start: Date, end: Date): string {
  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  const s = (d: Date) =>
    `${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
  if (sameMonth) {
    return `${start.getDate()} – ${end.getDate()} ${MONTH_NAMES[start.getMonth()]} ${start.getFullYear()}`;
  }
  if (sameYear) {
    return `${start.getDate()} ${MONTH_NAMES[start.getMonth()]} – ${end.getDate()} ${MONTH_NAMES[end.getMonth()]} ${end.getFullYear()}`;
  }
  return `${s(start)} – ${s(end)}`;
}

/**
 * Add N columns of `xStep` hours to a date, calendar-aware. For day-zooms
 * (xStep is a whole multiple of 24h), we step by calendar DAYS — this is
 * what DHTMLX does for its column rendering, and unlike fixed-millisecond
 * arithmetic it doesn't drift by 1 hour across DST boundaries.
 */
function addColumns(date: Date, cols: number, xStep: number): Date {
  const result = new Date(date);
  if (xStep >= 24 && xStep % 24 === 0) {
    result.setDate(result.getDate() + Math.round(cols * (xStep / 24)));
  } else {
    // Sub-day zoom: setHours with overflow handles DST per spec.
    result.setHours(result.getHours() + cols * xStep);
  }
  return result;
}

/**
 * Inverse of addColumns — how many columns separate `from` and `to`?
 * For day-zooms, count calendar days (DST-safe); otherwise hour arithmetic.
 */
function columnsBetween(from: Date, to: Date, xStep: number): number {
  if (xStep >= 24 && xStep % 24 === 0) {
    const fromMid = new Date(
      from.getFullYear(),
      from.getMonth(),
      from.getDate()
    );
    const toMid = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    const days = Math.round((toMid.getTime() - fromMid.getTime()) / 86400000);
    return days / (xStep / 24);
  }
  return (to.getTime() - from.getTime()) / (xStep * 3600 * 1000);
}

/**
 * Compute the date currently at the LEFT edge of the visible viewport,
 * given the scheduler anchor + scroll position. We use the left edge
 * (not the center) for both label resolution and snap positioning so the
 * snap button drops a calendar unit's first day cleanly at the left.
 * Returns null if state isn't ready yet.
 */
function getVisibleLeftDate(
  wrapper: HTMLElement,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  scheduler: any
): Date | null {
  const matrix = scheduler?.matrix?.timeline;
  const state = scheduler?.getState?.();
  if (!matrix || !(state?.date instanceof Date)) return null;
  const leftCol = wrapper.scrollLeft / COLUMN_WIDTH_PX;
  return addColumns(state.date, leftCol, matrix.x_step);
}

/**
 * Calendar-aligned label range derived from the LEFT edge of the visible
 * viewport. The label shows the "natural" unit containing that left edge:
 *   3D → 3 days starting from left's calendar day
 *   1W/2W/3W → Mon of week containing left, + N weeks
 *   1M/2M → 1st of month containing left, + N months
 *
 * Using the left edge means the snap button (which positions left = snap
 * start) results in a label that exactly matches the snap range, and the
 * label cleanly flips one unit forward whenever the user pans past the
 * natural boundary at the left.
 */
function getCalendarAlignedRange(
  leftDate: Date,
  zoomId: string
): { start: Date; end: Date } {
  if (zoomId === '1m' || zoomId === '2m') {
    const months = zoomId === '1m' ? 1 : 2;
    const start = new Date(
      leftDate.getFullYear(),
      leftDate.getMonth(),
      1,
      0,
      0,
      0,
      0
    );
    const end = new Date(start);
    end.setMonth(end.getMonth() + months);
    end.setMilliseconds(-1);
    return { start, end };
  }
  if (zoomId === '1w' || zoomId === '2w' || zoomId === '3w') {
    const weeks = zoomId === '1w' ? 1 : zoomId === '2w' ? 2 : 3;
    const start = getWeekMonday(leftDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + weeks * 7);
    end.setMilliseconds(-1);
    return { start, end };
  }
  // 3D — 3 days starting from leftDate's calendar day.
  const start = new Date(leftDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 3);
  end.setMilliseconds(-1);
  return { start, end };
}

/**
 * The full snap range for "snap to now" at a given zoom — what the user
 * sees the button do, computed in real calendar dates. The snap-action
 * code positions the LEFT edge of the viewport on this range's `start`.
 */
function getSnapRange(zoomId: string, today: Date): { start: Date; end: Date } {
  if (zoomId === '1m' || zoomId === '2m') {
    const months = zoomId === '1m' ? 1 : 2;
    const start = new Date(
      today.getFullYear(),
      today.getMonth(),
      1,
      0,
      0,
      0,
      0
    );
    const end = new Date(start);
    end.setMonth(end.getMonth() + months);
    end.setMilliseconds(-1);
    return { start, end };
  }
  if (zoomId === '1w' || zoomId === '2w' || zoomId === '3w') {
    const weeks = zoomId === '1w' ? 1 : zoomId === '2w' ? 2 : 3;
    const start = getWeekMonday(today);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + weeks * 7);
    end.setMilliseconds(-1);
    return { start, end };
  }
  // 3D — today and the next two days.
  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 3);
  end.setMilliseconds(-1);
  return { start, end };
}

/**
 * Dynamic button label — tells the user what "snap to now" actually does
 * at the current zoom.
 */
function getSnapButtonLabel(zoomId: string): string {
  switch (zoomId) {
    case '3d':
      return 'Today';
    case '1w':
      return 'This Week';
    case '2w':
      return 'Next 2 Weeks';
    case '3w':
      return 'Next 3 Weeks';
    case '1m':
      return 'This Month';
    case '2m':
      return 'Next 2 Months';
    default:
      return 'Today';
  }
}

// Get scheduler from window (attached by script tag)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const scheduler: any;

/**
 * Convert Epicor date (YYYY-MM-DD) and hour (decimal) to JavaScript Date
 */
function epicorDateTime(dateStr: string, hour: number): Date | null {
  if (!dateStr || hour == null) return null;

  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;

  d.setMinutes(d.getMinutes() + hour * 60);
  return d;
}

/**
 * Extract unique resources from schedule data with descriptions
 */
function extractResources(entries: SchedulingBoardEntry[]) {
  // Build a map of ResourceID -> ResourceIDDesc
  const resourceDescMap = new Map<string, string>();
  for (const entry of entries) {
    if (entry?.ResourceID) {
      const id = String(entry.ResourceID).trim();
      if (id && !resourceDescMap.has(id) && entry.ResourceIDDesc) {
        resourceDescMap.set(id, String(entry.ResourceIDDesc).trim());
      }
    }
  }

  // Get unique resource IDs
  const uniqueIds = Array.from(
    new Set(
      entries
        .map((r) => r?.ResourceID)
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
        .map((v) => String(v).trim())
    )
  ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  // Create resources with labels including description (below resource name)
  return uniqueIds.map((id) => {
    const desc = resourceDescMap.get(id);
    return {
      key: id,
      label: desc
        ? `<div class="resource-label"><span class="resource-id">${id}</span><span class="resource-desc">${desc}</span></div>`
        : id,
    };
  });
}

/**
 * Transform Epicor scheduling data to DHTMLX Scheduler events
 */
// Extended event type with full scheduling data
interface SchedulerEvent {
  id: string;
  text: string;
  start_date: Date;
  end_date: Date;
  progress: number;
  section_id: string;
  // Full entry data for popup
  entry: SchedulingBoardEntry;
}

function transformToSchedulerEvents(
  entries: SchedulingBoardEntry[]
): SchedulerEvent[] {
  return entries
    .filter(
      (r) =>
        r?.ResourceID !== null &&
        r?.ResourceID !== undefined &&
        String(r.ResourceID).trim() !== ''
    )
    .map((r, idx) => {
      const start = epicorDateTime(r.OSDate, r.OSHour);
      const end = epicorDateTime(r.ODDate, r.ODHour);

      // Safety fallback
      if (!start || !end || end <= start) return null;

      return {
        id:
          r?.SysRowID ??
          `${String(r.ResourceID).trim()}-${r?.JobNum ?? 'job'}-${r?.OprNum ?? 'op'}-${idx}`,
        text: `${r?.JobNum ?? ''} OP${r?.OprNum ?? ''}`.trim(),
        start_date: start,
        end_date: end,
        progress: (r?.PrctCmplt ?? 0) / 100,
        section_id: String(r.ResourceID).trim(),
        entry: r,
      };
    })
    .filter((e): e is SchedulerEvent => e !== null);
}

/**
 * Convert calendar hours array to closed time zones per day
 * Returns an object with day index (0-6) as key and array of [startMinute, endMinute] zones
 */
function getClosedZonesPerDay(calendarHours: boolean[]): Map<number, number[]> {
  const closedZones = new Map<number, number[]>();

  for (let day = 0; day < 7; day++) {
    const zones: number[] = [];
    let inClosedRange = false;
    let rangeStart = 0;

    for (let hour = 0; hour < 24; hour++) {
      const index = day * 24 + hour;
      const isClosed = !calendarHours[index]; // false = closed

      if (isClosed && !inClosedRange) {
        // Start of closed range
        inClosedRange = true;
        rangeStart = hour * 60;
      } else if (!isClosed && inClosedRange) {
        // End of closed range
        inClosedRange = false;
        zones.push(rangeStart, hour * 60);
      }
    }

    // Close any open range at end of day
    if (inClosedRange) {
      zones.push(rangeStart, 24 * 60);
    }

    if (zones.length > 0) {
      closedZones.set(day, zones);
    }
  }

  return closedZones;
}

export const ProductionSchedulePage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const schedulerContainer = useRef<HTMLDivElement>(null);
  const schedulerInitialized = useRef(false);
  const viewChangeHandlerId = useRef<string | null>(null);

  // Production calendar state for working hours
  const [_calendar, setCalendar] = useState<ProductionCalendar | null>(null);
  const calendarRef = useRef<ProductionCalendar | null>(null);

  // Selected event for popup
  const [selectedEvent, setSelectedEvent] = useState<SchedulerEvent | null>(
    null
  );

  // Get resource group filter from URL params, default to TN
  const [selectedFilter, setSelectedFilter] = useState<string>(() => {
    const filterParam = searchParams.get(URL_PARAM_FILTER);
    if (
      filterParam &&
      RESOURCE_GROUP_FILTERS.some((f) => f.id === filterParam)
    ) {
      return filterParam;
    }
    return DEFAULT_FILTER;
  });

  // Zoom level state
  const [zoomLevel, setZoomLevel] = useState<string>(DEFAULT_ZOOM);

  // Get view date from URL params, default to current week's Monday
  const viewDate = useMemo(() => {
    const weekParam = searchParams.get(URL_PARAM_WEEK);
    if (weekParam) {
      // Parse as local date to avoid timezone issues
      // YYYY-MM-DD format is interpreted as UTC by new Date(), causing off-by-one errors
      const [year, month, day] = weekParam.split('-').map(Number);
      if (year && month && day) {
        const parsed = new Date(year, month - 1, day); // month is 0-indexed
        if (!isNaN(parsed.getTime())) {
          return getWeekMonday(parsed);
        }
      }
    }
    return getWeekMonday(new Date());
  }, [searchParams]);

  // Stable "today" for data fetching - doesn't change when navigating
  const today = useMemo(() => getWeekMonday(new Date()), []);

  // Use schedule data loading with resource group filter
  // Pass "today" not "viewDate" so navigation doesn't trigger refetch
  const {
    data: scheduleData,
    isLoadingCurrent,
    isLoadingBackground,
    error,
    refetch,
    ensureWeekLoaded,
  } = useScheduleData(today, WEEKS_BEFORE, WEEKS_AHEAD, selectedFilter);

  /**
   * Handle filter change
   */
  const handleFilterChange = useCallback(
    (filterId: string) => {
      setSelectedFilter(filterId);
      // Update URL with new filter
      const weekParam = searchParams.get(URL_PARAM_WEEK);
      const newParams: Record<string, string> = {
        [URL_PARAM_FILTER]: filterId,
      };
      if (weekParam) {
        newParams[URL_PARAM_WEEK] = weekParam;
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  /**
   * Get current zoom level index
   */
  const zoomIndex = useMemo(
    () => ZOOM_LEVELS.findIndex((z) => z.id === zoomLevel),
    [zoomLevel]
  );

  /**
   * Handle zoom level change
   */
  const handleZoomChange = useCallback((zoomId: string) => {
    setZoomLevel(zoomId);
    const zoom = ZOOM_LEVELS.find((z) => z.id === zoomId);
    if (
      !zoom ||
      !schedulerInitialized.current ||
      typeof scheduler === 'undefined'
    ) {
      return;
    }

    // Capture the date currently at the visible CENTER (the user's focus)
    // so we can snap to the natural calendar unit containing it under the
    // new zoom.
    const wrapper = schedulerContainer.current?.querySelector(
      '.dhx_timeline_data_wrapper'
    ) as HTMLElement | null;
    const oldMatrix = scheduler.matrix.timeline;
    const oldAnchor: Date = scheduler.getState()?.date ?? new Date();
    let visibleCenter: Date = oldAnchor;
    if (wrapper) {
      const centerCol =
        (wrapper.scrollLeft + wrapper.clientWidth / 2) / COLUMN_WIDTH_PX;
      visibleCenter = addColumns(oldAnchor, centerCol, oldMatrix.x_step);
    }

    // Snap the new visible LEFT to the start of the natural calendar unit
    // (for the new zoom) that contains the user's previous focus. This
    // keeps them in roughly the same time period and lands them cleanly on
    // a Mon / 1st-of-month / day boundary instead of mid-hour.
    const newVisibleLeft = getCalendarAlignedRange(visibleCenter, zoomId).start;

    // Render RENDERED_WINDOWS × window_columns total columns.
    const windowCols = zoom.x_size;
    scheduler.matrix.timeline.x_step = zoom.x_step;
    scheduler.matrix.timeline.x_size = windowCols * RENDERED_WINDOWS;
    scheduler.matrix.timeline.x_length = windowCols * RENDERED_WINDOWS;
    scheduler.matrix.timeline.x_date = zoom.x_date;

    // Anchor positioned so newVisibleLeft lands exactly at the LEFT edge of
    // the middle rendered window. Calendar-aware to avoid DST drift.
    const newAnchor = addColumns(
      newVisibleLeft,
      -INITIAL_VISIBLE_INDEX * windowCols,
      zoom.x_step
    );
    scheduler.setCurrentView(newAnchor, 'timeline');

    // Position scrollLeft so the viewport's LEFT edge sits exactly on
    // newVisibleLeft.
    requestAnimationFrame(() => {
      const w = schedulerContainer.current?.querySelector(
        '.dhx_timeline_data_wrapper'
      ) as HTMLElement | null;
      if (w) {
        const startCol = columnsBetween(newAnchor, newVisibleLeft, zoom.x_step);
        w.scrollLeft = Math.max(0, startCol * COLUMN_WIDTH_PX);
      }
    });
  }, []);

  /**
   * Handle zoom slider change (slider is reversed: left=out, right=in)
   */
  const handleZoomSlider = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const sliderValue = parseInt(e.target.value, 10);
      // Reverse: slider max = index 0 (most zoomed in)
      const index = ZOOM_LEVELS.length - 1 - sliderValue;
      if (index >= 0 && index < ZOOM_LEVELS.length) {
        handleZoomChange(ZOOM_LEVELS[index].id);
      }
    },
    [handleZoomChange]
  );

  /**
   * Zoom in (decrease index = more detail)
   */
  const handleZoomIn = useCallback(() => {
    if (zoomIndex > 0) {
      handleZoomChange(ZOOM_LEVELS[zoomIndex - 1].id);
    }
  }, [zoomIndex, handleZoomChange]);

  /**
   * Zoom out (increase index = less detail)
   */
  const handleZoomOut = useCallback(() => {
    if (zoomIndex < ZOOM_LEVELS.length - 1) {
      handleZoomChange(ZOOM_LEVELS[zoomIndex + 1].id);
    }
  }, [zoomIndex, handleZoomChange]);

  /**
   * Snap the view to "now" — meaning the natural calendar unit for the
   * current zoom (this week / this month / today / etc.). Positions the
   * visible center inside the first natural unit of that snap range so the
   * calendar-aligned label resolves to the right thing.
   */
  const handleGoToThisWeek = useCallback(() => {
    if (typeof scheduler === 'undefined') return;
    const matrix = scheduler.matrix?.timeline;
    if (!matrix) return;
    const today = new Date();
    const snapRange = getSnapRange(zoomLevel, today);

    const windowCols = matrix.x_size / RENDERED_WINDOWS;
    const xStep = matrix.x_step;
    // Anchor positioned so snap.start lands exactly at the LEFT edge of the
    // middle rendered window. Calendar-aware (addColumns) to avoid DST drift.
    const newAnchor = addColumns(
      snapRange.start,
      -INITIAL_VISIBLE_INDEX * windowCols,
      xStep
    );
    scheduler.setCurrentView(newAnchor, 'timeline');

    requestAnimationFrame(() => {
      const wrapper = schedulerContainer.current?.querySelector(
        '.dhx_timeline_data_wrapper'
      ) as HTMLElement | null;
      if (wrapper) {
        const startCol = columnsBetween(newAnchor, snapRange.start, xStep);
        wrapper.scrollLeft = Math.max(0, startCol * COLUMN_WIDTH_PX);
      }
      if (updateUrlWeekRef.current) {
        updateUrlWeekRef.current(snapRange.start);
      }
    });
  }, [zoomLevel]);

  // Tracks the most recent ?week=... value we wrote ourselves. The
  // back/forward sync effect below uses this to distinguish a URL change
  // we caused (skip — view is already there) from one the browser caused
  // (re-position the scheduler).
  const lastUrlWeekWrittenByUsRef = useRef<string | null>(null);

  /**
   * Update URL with current week (preserves filter)
   */
  const updateUrlWeek = useCallback(
    (date: Date) => {
      const monday = getWeekMonday(date);
      const weekStr = formatDateKey(monday);
      lastUrlWeekWrittenByUsRef.current = weekStr;
      setSearchParams(
        {
          [URL_PARAM_WEEK]: weekStr,
          [URL_PARAM_FILTER]: selectedFilter,
        },
        { replace: true }
      );
    },
    [setSearchParams, selectedFilter]
  );

  // Refs for callbacks (avoid stale closures in event handlers)
  const updateUrlWeekRef = useRef(updateUrlWeek);
  const ensureWeekLoadedRef = useRef(ensureWeekLoaded);

  useEffect(() => {
    updateUrlWeekRef.current = updateUrlWeek;
  }, [updateUrlWeek]);

  useEffect(() => {
    ensureWeekLoadedRef.current = ensureWeekLoaded;
  }, [ensureWeekLoaded]);

  // Cooldown timestamp for edge-triggered rebases (survives effect re-runs)
  const lastEdgeJumpAtRef = useRef<number>(0);
  // True while a silent rebase is in flight — onViewChange skips URL update
  // and the per-week ensureWeekLoaded call (we do those manually with the
  // correct outer-edge weeks).
  const silentRebaseInProgressRef = useRef<boolean>(false);
  // Latest zoom level for long-lived closures (DHTMLX templates, drag/wheel
  // handlers) that outlive a single render. Updated in a useEffect below.
  const zoomLevelRef = useRef(zoomLevel);
  useEffect(() => {
    zoomLevelRef.current = zoomLevel;
  }, [zoomLevel]);

  // Browser back/forward sync — when the URL's ?week changes via a route
  // event we didn't initiate (back button, forward button, manual edit),
  // re-position the scheduler so the visible LEFT lands on viewDate.
  useEffect(() => {
    if (!schedulerInitialized.current || typeof scheduler === 'undefined') {
      return;
    }
    const matrix = scheduler.matrix?.timeline;
    if (!matrix) return;

    const viewDateStr = formatDateKey(viewDate);
    if (lastUrlWeekWrittenByUsRef.current === viewDateStr) {
      // This URL change was triggered by our own updateUrlWeek — the view
      // is already where it should be.
      return;
    }

    // External navigation: snap visible-left onto viewDate.
    const windowCols = matrix.x_size / RENDERED_WINDOWS;
    const xStep = matrix.x_step;
    const newAnchor = addColumns(
      viewDate,
      -INITIAL_VISIBLE_INDEX * windowCols,
      xStep
    );
    scheduler.setCurrentView(newAnchor, 'timeline');
    requestAnimationFrame(() => {
      const wrapper = schedulerContainer.current?.querySelector(
        '.dhx_timeline_data_wrapper'
      ) as HTMLElement | null;
      if (wrapper) {
        const startCol = columnsBetween(newAnchor, viewDate, xStep);
        wrapper.scrollLeft = Math.max(0, startCol * COLUMN_WIDTH_PX);
      }
    });
  }, [viewDate]);

  // Apply closed hour markers using addMarkedTimespan
  const applyClosedHourMarkers = useCallback((hours: boolean[]) => {
    if (!hours || hours.length !== 168) return;

    // Clear any existing marked timespans
    scheduler.deleteMarkedTimespan();

    // Get closed zones for each day
    const closedZones = getClosedZonesPerDay(hours);

    // Add marked timespan for each day with closed hours
    closedZones.forEach((zones, day) => {
      scheduler.addMarkedTimespan({
        days: [day],
        zones: zones,
        css: 'closed-hour-cell',
      });
    });

    scheduler.updateView();
  }, []);

  // Fetch production calendar for working hours
  useEffect(() => {
    const fetchCalendar = async () => {
      try {
        const cal = await apiService.getProductionCalendar(CALENDAR_ID);
        setCalendar(cal);
        calendarRef.current = cal;
        // Apply marked timespans if scheduler is already initialized
        if (schedulerInitialized.current && typeof scheduler !== 'undefined') {
          applyClosedHourMarkers(cal.hours);
        }
      } catch (err) {
        console.error('Failed to fetch production calendar:', err);
        // Non-fatal - scheduler still works without closed hour styling
      }
    };
    fetchCalendar();
  }, [applyClosedHourMarkers]);

  // Initialize DHTMLX Scheduler
  useEffect(() => {
    if (
      !schedulerContainer.current ||
      isLoadingCurrent ||
      scheduleData.length === 0
    ) {
      return;
    }

    // Enable plugins (limit must be before timeline for marked timespans)
    scheduler.plugins({
      limit: true,
      timeline: true,
    });

    // Extract resources from data
    const resources = extractResources(scheduleData);

    // Initialize or reinitialize scheduler
    if (!schedulerInitialized.current) {
      // Configure scheduler
      scheduler.locale.labels.timeline_tab = 'Timeline';
      scheduler.locale.labels.section_custom = 'Section';
      scheduler.config.details_on_dblclick = true;
      scheduler.config.default_date = '%j %M %Y';
      scheduler.config.readonly = false;
      // Disable event creation on click/double-click
      scheduler.config.dblclick_create = false;
      scheduler.config.drag_create = false;

      // Create a named collection for resources (enables dynamic updates)
      scheduler.serverList('timeline_resources', resources);

      // Create timeline view. In DHTMLX, `x_size` is the TOTAL number of
      // rendered columns — the visible portion is whatever fits in the
      // container width. We render RENDERED_WINDOWS × WINDOW_COLUMNS columns
      // so the user can pan smoothly through 3 windows of content before a
      // silent rebase is needed.
      const WINDOW_COLUMNS_DEFAULT = (7 * 24) / 6; // 28 cols = 1 week at 6h/col
      scheduler.createTimelineView({
        name: 'timeline',
        x_unit: 'hour',
        x_step: 6,
        x_size: WINDOW_COLUMNS_DEFAULT * RENDERED_WINDOWS,
        x_length: WINDOW_COLUMNS_DEFAULT * RENDERED_WINDOWS,
        x_date: '%H',
        x_start: 0,
        // Second header row (top): days
        second_scale: {
          x_unit: 'day',
          x_date: '%D %d',
        },
        y_unit: scheduler.serverList('timeline_resources'),
        y_property: 'section_id',
        render: 'bar',
        dy: 72, // Row height increased by ~20% from default 60
        scrollable: true,
        column_width: COLUMN_WIDTH_PX,
      });

      // Override the navbar date label to show the *visible* range, not the
      // full rendered strip (which is 3× the visible window under the
      // continuous-scroll layout). Template is invoked when the view re-
      // renders; scroll updates separately update .dhx_cal_date below.
      scheduler.templates.timeline_date = function (start: Date) {
        const wrapper = schedulerContainer.current?.querySelector(
          '.dhx_timeline_data_wrapper'
        ) as HTMLElement | null;
        if (!wrapper) {
          // Fall back: align to current zoom around the anchor itself.
          const range = getCalendarAlignedRange(start, zoomLevelRef.current);
          return formatDateRangeLabel(range.start, range.end);
        }
        const leftDate = getVisibleLeftDate(wrapper, scheduler);
        if (!leftDate) return formatDateRangeLabel(start, start);
        const range = getCalendarAlignedRange(leftDate, zoomLevelRef.current);
        return formatDateRangeLabel(range.start, range.end);
      };

      // Custom event bar template with progress
      scheduler.templates.event_bar_text = function (
        start: Date,
        end: Date,
        ev: { progress?: number; text?: string }
      ) {
        const pct = Math.max(0, Math.min(1, ev.progress ?? 0));
        const w = Math.round(pct * 100);
        return `
          <div class="ev-progress" style="width:${w}%"></div>
          <div class="ev-text">${ev.text ?? ''}</div>
        `;
      };

      // Initialize scheduler. We shift the DHTMLX anchor BACKWARD by
      // INITIAL_VISIBLE_INDEX "windows" so the user's intended viewDate lands
      // in the middle of the rendered strip (with one window of buffer to
      // the left for pan-back, and one window to the right for pan-forward).
      const initStep = scheduler.matrix?.timeline?.x_step ?? 6;
      const initWindowCols = WINDOW_COLUMNS_DEFAULT;
      // Calendar-aware shift so the anchor lands at midnight local time
      // (otherwise crossing DST puts it at 23:00 the day before).
      const initAnchor = addColumns(
        viewDate,
        -INITIAL_VISIBLE_INDEX * initWindowCols,
        initStep
      );
      scheduler.init(schedulerContainer.current, initAnchor, 'timeline');
      schedulerInitialized.current = true;

      // Position scrollLeft so the visible area shows the user's viewDate
      // (i.e. the middle of the rendered strip). Defer to next tick so the
      // wrapper exists in the DOM after DHTMLX's synchronous render.
      // Also write the URL explicitly here so refreshes restore the same
      // visible week — the scroll listener that normally maintains URL only
      // fires once the user actually scrolls.
      requestAnimationFrame(() => {
        const wrapper = schedulerContainer.current?.querySelector(
          '.dhx_timeline_data_wrapper'
        ) as HTMLElement | null;
        if (wrapper) {
          wrapper.scrollLeft =
            INITIAL_VISIBLE_INDEX * initWindowCols * COLUMN_WIDTH_PX;
        }
        if (updateUrlWeekRef.current) {
          updateUrlWeekRef.current(viewDate);
        }
      });

      // Apply closed hour markers if calendar is already loaded
      if (calendarRef.current?.hours) {
        applyClosedHourMarkers(calendarRef.current.hours);
      }

      // Detach previous handler if exists (prevent duplicate handlers)
      if (viewChangeHandlerId.current) {
        scheduler.detachEvent(viewChangeHandlerId.current);
      }

      // Skip the first onViewChange (fires during init)
      let isFirstViewChange = true;

      // Attach view change handler. URL updates live in the scroll listener
      // (visible-center-based, debounced); here we only kick off any missing
      // ensureWeekLoaded calls for the rendered strip when a user-initiated
      // navigation (prev/next button, This Week, zoom) lands us somewhere
      // beyond the current cache.
      viewChangeHandlerId.current = scheduler.attachEvent(
        'onViewChange',
        (newMode: string, newDate: Date) => {
          if (isFirstViewChange) {
            isFirstViewChange = false;
            return true;
          }
          if (silentRebaseInProgressRef.current) {
            return true;
          }
          if (ensureWeekLoadedRef.current) {
            const matrix = scheduler.matrix?.timeline;
            if (matrix) {
              const endDate = addColumns(newDate, matrix.x_size, matrix.x_step);
              for (
                let t = newDate.getTime();
                t < endDate.getTime();
                t += 7 * 24 * 3600 * 1000
              ) {
                ensureWeekLoadedRef.current(new Date(t));
              }
              ensureWeekLoadedRef.current(endDate);
            } else {
              ensureWeekLoadedRef.current(newDate);
            }
          }
          return true;
        }
      );

      // Prevent event creation on empty cell click
      scheduler.attachEvent('onEmptyClick', () => {
        return false; // Prevent default lightbox opening
      });

      // Prevent lightbox from opening - we show our own popup
      scheduler.attachEvent('onBeforeLightbox', () => {
        return false;
      });

      // Handle click on events - show popup
      scheduler.attachEvent('onClick', (id: string) => {
        if (id) {
          const ev = scheduler.getEvent(id);
          if (ev?.entry) {
            setSelectedEvent(ev as SchedulerEvent);
          }
        }
        return true;
      });

      // Block double-click on empty areas
      scheduler.attachEvent('onDblClick', (id: string) => {
        if (!id) {
          return false;
        }
        // Double-click on event also shows popup
        const ev = scheduler.getEvent(id);
        if (ev?.entry) {
          setSelectedEvent(ev as SchedulerEvent);
        }
        return false; // Prevent default lightbox
      });
    } else {
      // Already initialized - update resources dynamically
      scheduler.updateCollection('timeline_resources', resources);
    }

    // ALWAYS clear existing events before parsing new ones to prevent duplicates
    scheduler.clearAll();

    // Load events
    const events = transformToSchedulerEvents(scheduleData);
    scheduler.parse(events);
  }, [
    scheduleData,
    isLoadingCurrent,
    viewDate,
    selectedFilter,
    applyClosedHourMarkers,
  ]);

  const handleRefresh = () => {
    refetch(true);
  };

  // Setup drag-to-scroll for horizontal panning + edge-trigger week jump.
  // DHTMLX recreates the .dhx_timeline_data_wrapper element on certain
  // operations (e.g. scheduler.updateView() triggered by closed-hour markers,
  // zoom, etc.), so a one-shot useEffect attach would land on a dead element.
  // Watch the container with a MutationObserver and re-attach whenever a new
  // wrapper appears.
  useEffect(() => {
    const container = schedulerContainer.current;
    if (!container) return;

    let currentArea: HTMLElement | null = null;
    let detach: (() => void) | null = null;

    const attachTo = (dataArea: HTMLElement) => {
      let isDragging = false;
      let startX = 0;
      let dragStartScrollLeft = 0;

      // Trigger a silent rebase when the visible window approaches within
      // this fraction of one "logical window" of either strip edge. 0.5 means
      // we rebase when the user has half a window of buffer left.
      const REBASE_THRESHOLD_RATIO = 0.5;
      // Minimum gap between rebases so we don't fire on every scroll frame.
      const REBASE_COOLDOWN_MS = 250;

      const trySilentRebase = (direction: 'prev' | 'next') => {
        const now = Date.now();
        if (now - lastEdgeJumpAtRef.current < REBASE_COOLDOWN_MS) return;
        if (typeof scheduler === 'undefined') return;
        // Skip rebase while a drag is in flight — DHTMLX may replace the
        // wrapper element, losing the drag state. The handleMouseUp below
        // re-fires a scroll event so the rebase still happens on release if
        // the user was past the trigger threshold.
        if (isDragging) return;
        const matrix = scheduler.matrix?.timeline;
        if (!matrix) return;

        const xStep: number = matrix.x_step;
        const xSize: number = matrix.x_size; // total rendered columns
        const windowCols = xSize / RENDERED_WINDOWS; // one logical window
        const shiftPx = windowCols * COLUMN_WIDTH_PX;

        const state = scheduler.getState?.();
        const currentAnchor: Date =
          state?.date instanceof Date ? state.date : new Date();
        // Calendar-aware shift so anchor doesn't drift across DST boundaries.
        const newAnchor = addColumns(
          currentAnchor,
          direction === 'next' ? windowCols : -windowCols,
          xStep
        );

        // Capture scroll BEFORE rebase, then restore it shifted so the same
        // content is under the user's eye visually.
        const beforeScroll = dataArea.scrollLeft;
        const targetScroll =
          direction === 'next'
            ? beforeScroll - shiftPx
            : beforeScroll + shiftPx;

        lastEdgeJumpAtRef.current = now;
        silentRebaseInProgressRef.current = true;
        scheduler.setCurrentView(newAnchor, 'timeline');
        silentRebaseInProgressRef.current = false;

        // After DHTMLX re-renders (possibly replacing the wrapper), find the
        // new wrapper and set its scrollLeft so visually nothing moved.
        requestAnimationFrame(() => {
          const w = container.querySelector(
            '.dhx_timeline_data_wrapper'
          ) as HTMLElement | null;
          if (w) {
            const maxScroll = w.scrollWidth - w.clientWidth;
            w.scrollLeft = Math.max(0, Math.min(maxScroll, targetScroll));
          }
          // If a drag was in progress, adjust its baseline so the next
          // mousemove computes against the new scrollLeft.
          if (isDragging) {
            dragStartScrollLeft =
              direction === 'next'
                ? dragStartScrollLeft - shiftPx
                : dragStartScrollLeft + shiftPx;
          }
        });

        // Eagerly load any week newly exposed by this rebase. The strip now
        // covers [newAnchor, newAnchor + xSize*xStep hours]. The newly-needed
        // outer chunk is one window's worth at the leading edge, which may
        // span 1–2 weeks depending on alignment. Fire ensureWeekLoaded in
        // parallel for each week in that range (the hook dedups in-flight
        // and cached requests).
        const outerStart =
          direction === 'next'
            ? addColumns(newAnchor, (RENDERED_WINDOWS - 1) * windowCols, xStep)
            : newAnchor;
        const outerEnd = addColumns(outerStart, windowCols, xStep);
        if (ensureWeekLoadedRef.current) {
          for (
            let t = outerStart.getTime();
            t < outerEnd.getTime();
            t += 7 * 24 * 3600 * 1000
          ) {
            ensureWeekLoadedRef.current(new Date(t));
          }
          // Ensure the chunk's end week is also covered (in case the range
          // straddles a week boundary inside the last 7-day step).
          ensureWeekLoadedRef.current(outerEnd);
        }
      };

      // Native scroll handler — the gate for silent rebase. Fires on every
      // pan/wheel/keyboard-scroll, so we throttle via rAF and check the
      // distance from each strip edge.
      // Refresh the .dhx_cal_date label to reflect the current visible range.
      // DHTMLX's template only fires on view re-renders, but the visible
      // range changes on every scroll — we update the DOM directly here.
      const updateDateLabel = () => {
        const dateEl = container.querySelector(
          '.dhx_cal_date'
        ) as HTMLElement | null;
        if (!dateEl) return;
        const leftDate = getVisibleLeftDate(dataArea, scheduler);
        if (!leftDate) return;
        const range = getCalendarAlignedRange(leftDate, zoomLevelRef.current);
        dateEl.textContent = formatDateRangeLabel(range.start, range.end);
      };

      let scrollRafPending = false;
      const handleScroll = () => {
        if (scrollRafPending) return;
        scrollRafPending = true;
        requestAnimationFrame(() => {
          scrollRafPending = false;
          updateDateLabel();
          const matrix = scheduler.matrix?.timeline;
          if (!matrix) return;
          const windowPx = (matrix.x_size / RENDERED_WINDOWS) * COLUMN_WIDTH_PX;
          const triggerPx = windowPx * REBASE_THRESHOLD_RATIO;
          const sl = dataArea.scrollLeft;
          const maxScroll = dataArea.scrollWidth - dataArea.clientWidth;
          if (sl < triggerPx) {
            trySilentRebase('prev');
          } else if (sl > maxScroll - triggerPx) {
            trySilentRebase('next');
          }
        });
      };

      // Initial sync — covers the moment between attach and the user's first
      // scroll, where the template's fallback was used.
      requestAnimationFrame(updateDateLabel);

      // Debounced URL update — reflects the date currently at the visible
      // center, so refreshing the page lands you back where you were.
      let urlUpdateTimer: ReturnType<typeof setTimeout> | null = null;
      const handleScrollForUrl = () => {
        if (urlUpdateTimer) clearTimeout(urlUpdateTimer);
        urlUpdateTimer = setTimeout(() => {
          if (silentRebaseInProgressRef.current) return;
          const matrix = scheduler.matrix?.timeline;
          const state = scheduler.getState?.();
          if (!matrix || !(state?.date instanceof Date)) return;
          const centerCol =
            (dataArea.scrollLeft + dataArea.clientWidth / 2) / COLUMN_WIDTH_PX;
          const centerDate = new Date(
            state.date.getTime() + centerCol * matrix.x_step * 3600 * 1000
          );
          // updateUrlWeek calls getWeekMonday internally, so the raw center
          // date is fine.
          if (updateUrlWeekRef.current) {
            updateUrlWeekRef.current(centerDate);
          }
        }, 500);
      };

      // Drag-pan — pure native scroll. No edge trigger; the scroll handler
      // above will rebase silently as needed.
      const handleMouseDown = (e: MouseEvent) => {
        if (e.button !== 0) return;
        const target = e.target as HTMLElement;
        if (target.closest('.dhx_cal_event_line')) return;
        isDragging = true;
        startX = e.pageX - dataArea.getBoundingClientRect().left;
        dragStartScrollLeft = dataArea.scrollLeft;
        dataArea.style.cursor = 'grabbing';
        e.preventDefault();
      };
      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        const x = e.pageX - dataArea.getBoundingClientRect().left;
        const walk = (x - startX) * 1.5;
        const maxScroll = dataArea.scrollWidth - dataArea.clientWidth;
        dataArea.scrollLeft = Math.max(
          0,
          Math.min(maxScroll, dragStartScrollLeft - walk)
        );
      };
      const handleMouseUp = () => {
        if (!isDragging) return;
        isDragging = false;
        dataArea.style.cursor = 'grab';
        // If the user dragged into an edge while we were skipping rebase,
        // fire one now that the drag is over.
        handleScroll();
      };
      const handleMouseLeave = () => {
        if (!isDragging) return;
        isDragging = false;
        dataArea.style.cursor = 'grab';
        handleScroll();
      };

      // Shift+wheel → native horizontal scroll. The scroll listener above
      // takes care of triggering rebase near the edges.
      const handleWheel = (e: WheelEvent) => {
        if (!e.shiftKey) return;
        e.preventDefault();
        const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
        const maxScroll = dataArea.scrollWidth - dataArea.clientWidth;
        dataArea.scrollLeft = Math.max(
          0,
          Math.min(maxScroll, dataArea.scrollLeft + delta)
        );
      };

      dataArea.style.cursor = 'grab';
      dataArea.addEventListener('mousedown', handleMouseDown);
      dataArea.addEventListener('mousemove', handleMouseMove);
      dataArea.addEventListener('mouseup', handleMouseUp);
      dataArea.addEventListener('mouseleave', handleMouseLeave);
      dataArea.addEventListener('wheel', handleWheel, { passive: false });
      dataArea.addEventListener('scroll', handleScroll);
      dataArea.addEventListener('scroll', handleScrollForUrl);

      return () => {
        dataArea.removeEventListener('mousedown', handleMouseDown);
        dataArea.removeEventListener('mousemove', handleMouseMove);
        dataArea.removeEventListener('mouseup', handleMouseUp);
        dataArea.removeEventListener('mouseleave', handleMouseLeave);
        dataArea.removeEventListener('wheel', handleWheel);
        dataArea.removeEventListener('scroll', handleScroll);
        dataArea.removeEventListener('scroll', handleScrollForUrl);
        if (urlUpdateTimer) clearTimeout(urlUpdateTimer);
      };
    };

    const sync = () => {
      const next = container.querySelector(
        '.dhx_timeline_data_wrapper'
      ) as HTMLElement | null;
      if (next === currentArea) return;
      if (detach) {
        detach();
        detach = null;
      }
      currentArea = next;
      if (next) {
        detach = attachTo(next);
      }
    };

    // Initial attach (covers the wrapper that already exists when the effect runs)
    sync();

    // Re-attach whenever DHTMLX swaps the wrapper element
    const observer = new MutationObserver(sync);
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (detach) detach();
      currentArea = null;
    };
    // Container is now always mounted (B5 fix), so the ref is set on first
    // mount and the MutationObserver picks up DHTMLX's wrapper as it gets
    // injected. No deps needed.
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 w-full">
      <div className="w-full">
        <Breadcrumb currentPage={t('productionSchedule.title')} />

        {/* Header */}
        <div className="mb-6 mt-4 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {t('productionSchedule.title')}
            </h1>
            <p className="mt-2 text-gray-600">
              {t(
                'productionSchedule.description',
                'View production schedule by resource'
              )}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* Resource Group Filter */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {RESOURCE_GROUP_FILTERS.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => handleFilterChange(filter.id)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    selectedFilter === filter.id
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            {/* Zoom Slider (left = zoomed out, right = zoomed in) */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-2 py-1">
              <button
                onClick={handleZoomOut}
                disabled={zoomIndex === ZOOM_LEVELS.length - 1}
                className="p-1 text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Zoom Out"
              >
                <Minus className="w-4 h-4" />
              </button>
              <input
                type="range"
                min={0}
                max={ZOOM_LEVELS.length - 1}
                value={ZOOM_LEVELS.length - 1 - zoomIndex}
                onChange={handleZoomSlider}
                className="w-24 h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer accent-gray-700"
              />
              <button
                onClick={handleZoomIn}
                disabled={zoomIndex === 0}
                className="p-1 text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Zoom In"
              >
                <Plus className="w-4 h-4" />
              </button>
              <span className="text-xs font-medium text-gray-600 w-8">
                {ZOOM_LEVELS[zoomIndex]?.label}
              </span>
            </div>
            {/* This Week Button */}
            <Button
              variant="outline"
              onClick={handleGoToThisWeek}
              className="text-sm"
            >
              {getSnapButtonLabel(zoomLevel)}
            </Button>
            {isLoadingBackground && (
              <div className="flex items-center gap-2 text-sm text-blue-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>
                  {t('productionSchedule.loadingData', 'Loading data...')}
                </span>
              </div>
            )}
            <Button
              onClick={handleRefresh}
              disabled={isLoadingCurrent}
              className="gap-2"
            >
              <RefreshCw
                className={`w-4 h-4 ${isLoadingCurrent ? 'animate-spin' : ''}`}
              />
              {t('common.refresh', 'Refresh')}
            </Button>
          </div>
        </div>

        {/* Error Message */}
        {error && !isLoadingCurrent && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 mb-6">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-red-900">
                {t('common.error', 'Error')}
              </h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Loading State — only on the very first load when there's nothing
            to show yet. Subsequent refreshes/filter switches keep the
            scheduler visible (refresh icon spins in the toolbar instead). */}
        {isLoadingCurrent && scheduleData.length === 0 && (
          <div className="p-8 bg-blue-50 border border-blue-200 rounded-lg text-center">
            <RefreshCw className="w-8 h-8 text-blue-600 mx-auto mb-3 animate-spin" />
            <p className="text-blue-700 font-medium">
              {t(
                'productionSchedule.loading',
                'Loading production schedule...'
              )}
            </p>
          </div>
        )}

        {/* Scheduler Container — always mounted so refresh/filter switches
            don't tear down DHTMLX state (scroll position, view date, etc).
            Hidden via display:none until first data arrives. */}
        {!error && (
          <div
            ref={schedulerContainer}
            className="dhx_cal_container"
            style={{
              width: '100%',
              height: 'calc(100vh - 220px)',
              minHeight: '500px',
              display: scheduleData.length === 0 ? 'none' : undefined,
            }}
          >
            <div className="dhx_cal_navline">
              <div className="dhx_cal_prev_button" />
              <div className="dhx_cal_next_button" />
              <div className="dhx_cal_today_button" />
              <div className="dhx_cal_date" />
            </div>
            <div className="dhx_cal_header" />
            <div className="dhx_cal_data" />
          </div>
        )}
      </div>

      {/* Custom styles for scheduler */}
      <style>{`
        .dhx_cal_data .dhx_cal_event,
        .dhx_cal_data .dhx_cal_event_line {
          border-radius: 6px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.15);
        }

        .dhx_cal_event_line {
          position: relative;
          overflow: hidden;
        }

        .ev-progress {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          pointer-events: none;
        }

        .ev-text {
          position: relative;
          padding: 2px 6px;
          z-index: 1;
        }

        /* Closed hour cells (facility not operating) */
        .closed-hour-cell {
          background-color: #e5e7eb !important;
          background-image: repeating-linear-gradient(
            45deg,
            transparent,
            transparent 4px,
            rgba(156, 163, 175, 0.3) 4px,
            rgba(156, 163, 175, 0.3) 8px
          );
        }

        /* Resource Y-axis cells - center content */
        .dhx_matrix_scell {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }

        /* Resource labels with description below */
        .resource-label {
          display: flex;
          flex-direction: column;
          line-height: 1.4;
        }

        .resource-id {
          font-weight: 600;
        }

        .resource-desc {
          font-size: 0.85em;
          color: #6b7280;
        }
      `}</style>

      {/* Task Details Popup */}
      {selectedEvent && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setSelectedEvent(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {selectedEvent.entry.JobNum} - OP{selectedEvent.entry.OprNum}
              </h2>
              <button
                onClick={() => setSelectedEvent(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Job Information */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Job Information
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="col-span-2">
                    <span className="text-gray-500">Part:</span>
                    <a
                      href={`/parts?partNum=${selectedEvent.entry.JobPartNum}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 font-medium text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {selectedEvent.entry.JobPartNumUM ||
                        selectedEvent.entry.JobPartNum}
                    </a>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500">Description:</span>
                    <span className="ml-2">
                      {selectedEvent.entry.JobPartDesc}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Qty Required:</span>
                    <span className="ml-2 font-medium">
                      {selectedEvent.entry.ProdQty}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Qty Completed:</span>
                    <span className="ml-2 font-medium">
                      {selectedEvent.entry.JobHeadQtyCompleted}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Due Date:</span>
                    <span className="ml-2">
                      {selectedEvent.entry.ReqDueDate
                        ? parseCalendarDate(
                            selectedEvent.entry.ReqDueDate
                          ).toLocaleDateString()
                        : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Days Late:</span>
                    <span
                      className={`ml-2 font-medium ${selectedEvent.entry.DaysLate > 0 ? 'text-red-600' : 'text-green-600'}`}
                    >
                      {selectedEvent.entry.DaysLate > 0
                        ? `${selectedEvent.entry.DaysLate} days`
                        : 'On time'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Operation Information */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Operation Details
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Op Code:</span>
                    <span className="ml-2 font-medium">
                      {selectedEvent.entry.OpCode}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Op #:</span>
                    <span className="ml-2 font-medium">
                      {selectedEvent.entry.OprNum}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500">Description:</span>
                    <span className="ml-2">{selectedEvent.entry.OpDesc}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Resource:</span>
                    <span className="ml-2 font-medium">
                      {selectedEvent.entry.ResourceID}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Resource Group:</span>
                    <span className="ml-2">
                      {selectedEvent.entry.ResourceGrpID}
                    </span>
                  </div>
                </div>
              </div>

              {/* Progress Information */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Progress
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Completion:</span>
                    <span className="ml-2 font-medium">
                      {selectedEvent.entry.PrctCmplt}%
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Op Qty Completed:</span>
                    <span className="ml-2 font-medium">
                      {selectedEvent.entry.JobOperQtyCompleted}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Est Hours:</span>
                    <span className="ml-2">
                      {selectedEvent.entry.TotalEstHours?.toFixed(1)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Actual Hours:</span>
                    <span className="ml-2">
                      {selectedEvent.entry.TotalActHours?.toFixed(1)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Remaining:</span>
                    <span className="ml-2">
                      {selectedEvent.entry.RemainHours?.toFixed(1)} hrs
                    </span>
                  </div>
                </div>
                {/* Progress Bar */}
                <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-600 rounded-full"
                    style={{ width: `${selectedEvent.entry.PrctCmplt}%` }}
                  />
                </div>
              </div>

              {/* Schedule Information */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Schedule
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Start:</span>
                    <span className="ml-2">
                      {selectedEvent.start_date.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">End:</span>
                    <span className="ml-2">
                      {selectedEvent.end_date.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
              <Button variant="outline" onClick={() => setSelectedEvent(null)}>
                Close
              </Button>
              <Button
                onClick={() => {
                  window.open(
                    `/job-tracker?jobNum=${selectedEvent.entry.JobNum}`,
                    '_blank'
                  );
                }}
                className="gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Job Tracker
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
